/**
 * Een gekozen foto klaarmaken voordat hij de deur uit gaat.
 *
 * <p>Twee dingen gaan hier mis zonder deze stap, allebei gezien bij een klant op 15-10-2026.
 *
 * <p><b>HEIC.</b> Dat is wat een iPhone standaard maakt, en niets in onze keten kan het lezen: de
 * browser niet, Java niet, en het AI-model ook niet. Een foto van een vaccinatieboekje die iemand van
 * zijn telefoon naar de pc haalt, kwam daardoor binnen als "Fout bij verwerken paspoortfoto". Omzetten
 * hoort hier, in de browser: op de server zou er een natieve bibliotheek (libheif) op elke klantserver
 * moeten staan, en dat wil je niet beheren. De omzetter is een halve megabyte aan WebAssembly, dus
 * die wordt pas opgehaald als er echt een HEIC langskomt.
 *
 * <p><b>Grootte.</b> Een bladzijde van een telefoon is 15 tot 35 MB. Dat is zonde van de upload, en
 * het model schaalt alles boven ongeveer 1570 pixels toch terug. Hier verkleinen scheelt de wachttijd
 * van het uploaden; de server doet het daarna nog een keer voor wat niet langs deze weg binnenkomt.
 *
 * <p>Alles hier is best-effort: lukt het omzetten of verkleinen niet, dan gaat het origineel eropuit.
 * Een foto die misschien nog leesbaar is, is beter dan een foutmelding van ons.
 */

/** De lange zijde waar we op uitkomen; gelijk aan wat de server aanhoudt. */
const MAX_ZIJDE = 1800;

/** Onder deze grootte heeft verkleinen geen zin. */
const KLEIN_GENOEG = 400 * 1024;

/**
 * Of dit een iPhone-foto is. Aan de inhoud herkend en niet aan de naam: een bestand dat via
 * WhatsApp of OneDrive komt, heet van alles, en de browser noemt het type soms helemaal niet.
 */
/**
 * De eerste bytes van een bestand.
 *
 * <p>Niet alleen {@code blob.arrayBuffer()}: die ontbreekt in oudere browsers (en in onze
 * testomgeving), en dan zou het herkennen stilletjes false teruggeven -- waarna een iPhone-foto
 * ongemoeid doorgaat en pas bij de klant stukloopt. FileReader bestaat overal.
 */
async function eersteBytes(bestand: File, aantal: number): Promise<Uint8Array> {
  const stuk = bestand.slice(0, aantal);
  if (typeof stuk.arrayBuffer === "function") {
    try {
      return new Uint8Array(await stuk.arrayBuffer());
    } catch {
      // door naar de FileReader
    }
  }
  return await new Promise<Uint8Array>((klaar) => {
    const lezer = new FileReader();
    lezer.onload = () => klaar(new Uint8Array(lezer.result as ArrayBuffer));
    lezer.onerror = () => klaar(new Uint8Array());
    lezer.readAsArrayBuffer(stuk);
  });
}

export async function isHeic(bestand: File): Promise<boolean> {
  const type = (bestand.type || "").toLowerCase();
  if (type.includes("heic") || type.includes("heif")) return true;
  if (/\.hei[cf]$/i.test(bestand.name)) return true;
  try {
    const kop = await eersteBytes(bestand, 12);
    if (kop.length < 12) return false;
    const ftyp = String.fromCharCode(kop[4], kop[5], kop[6], kop[7]);
    if (ftyp !== "ftyp") return false;
    const merk = String.fromCharCode(kop[8], kop[9], kop[10], kop[11]).toLowerCase();
    return merk.startsWith("hei") || merk.startsWith("mif") || merk.startsWith("msf") || merk.startsWith("hev");
  } catch {
    return false;
  }
}

/** De foto zoals hij verstuurd wordt: altijd een formaat dat de rest van de keten kent. */
export async function fotoKlaarzetten(bestand: File): Promise<File> {
  let werk = bestand;
  if (await isHeic(bestand)) {
    const omgezet = await heicNaarJpeg(bestand);
    if (!omgezet) return bestand; // laat de server er iets zinnigs over zeggen
    werk = omgezet;
  }
  return (await verklein(werk)) ?? werk;
}

async function heicNaarJpeg(bestand: File): Promise<File | null> {
  try {
    // Pas nu ophalen: de omzetter is groot en de meeste foto's zijn geen HEIC.
    const { default: heic2any } = await import("heic2any");
    const uit = await heic2any({ blob: bestand, toType: "image/jpeg", quality: 0.9 });
    const blob = Array.isArray(uit) ? uit[0] : uit;
    return new File([blob], bestand.name.replace(/\.hei[cf]$/i, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return null;
  }
}

/** Verkleinen via een canvas. Levert {@code null} als het niet lukt of niets oplevert. */
async function verklein(bestand: File): Promise<File | null> {
  if (bestand.size <= KLEIN_GENOEG || !bestand.type.startsWith("image/")) return null;
  let beeld: ImageBitmap;
  try {
    beeld = await createImageBitmap(bestand);
  } catch {
    return null;
  }
  try {
    const langste = Math.max(beeld.width, beeld.height);
    const factor = langste <= MAX_ZIJDE ? 1 : MAX_ZIJDE / langste;
    const breed = Math.max(1, Math.round(beeld.width * factor));
    const hoog = Math.max(1, Math.round(beeld.height * factor));
    const doek = document.createElement("canvas");
    doek.width = breed;
    doek.height = hoog;
    const penseel = doek.getContext("2d");
    if (!penseel) return null;
    // Wit eronder: jpeg kent geen doorzichtigheid, en die vlakken zouden zwart worden.
    penseel.fillStyle = "#ffffff";
    penseel.fillRect(0, 0, breed, hoog);
    penseel.drawImage(beeld, 0, 0, breed, hoog);
    const blob = await new Promise<Blob | null>((klaar) => doek.toBlob(klaar, "image/jpeg", 0.9));
    if (!blob || blob.size >= bestand.size) return null;
    return new File([blob], bestand.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } finally {
    beeld.close?.();
  }
}
